// src/utils/executive.utils.tsx
// Shared formatters for the Executive Dashboard.
// Currency/count/percent, Buddhist year conversion, relative-time.

import type { DatePreset, Granularity } from 'api/executive/executive.types'

export const formatCurrency = (v: number | null | undefined): string => {
  if (v == null) return 'N/A'
  return `฿${v.toLocaleString('th-TH', { maximumFractionDigits: 0 })}`
}

// Compact currency for headline KPI tiles where horizontal space is tight.
// Falls back to full format below 10M so most clinics' daily/weekly numbers
// keep their familiar appearance; only large multi-branch monthly totals
// abbreviate. The tile's tooltip should always carry the exact amount.
export const formatCurrencyCompact = (v: number | null | undefined): string => {
  if (v == null) return 'N/A'
  if (Math.abs(v) >= 10_000_000) {
    return `฿${(v / 1_000_000).toLocaleString('th-TH', { maximumFractionDigits: 2 })} ลบ.`
  }
  return formatCurrency(v)
}

export const formatCount = (v: number | null | undefined, unit = 'เคส'): string => {
  if (v == null) return 'N/A'
  return `${v.toLocaleString('th-TH')} ${unit}`
}

export const formatPercent = (v: number | null | undefined, decimals = 1): string => {
  if (v == null) return 'N/A'
  return `${v.toFixed(decimals)}%`
}

// Buddhist year = CE + 543
export const toBuddhistYear = (ceYear: number): number => ceYear + 543
export const toGregorianYear = (beYear: number): number => beYear - 543

export interface DateRange {
  start: string // YYYY-MM-DD
  end: string
}

// Local-date YMD to avoid UTC drift (Asia/Bangkok timezone concern)
const toLocalYMD = (d: Date): string => {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export const resolvePreset = (preset: DatePreset, today: Date = new Date()): DateRange | null => {
  const y = today.getFullYear()
  const m = today.getMonth()
  const d = today.getDate()

  switch (preset) {
    case 'today': {
      const t = new Date(y, m, d)
      return { start: toLocalYMD(t), end: toLocalYMD(t) }
    }
    case 'yesterday': {
      const y0 = new Date(y, m, d - 1)
      return { start: toLocalYMD(y0), end: toLocalYMD(y0) }
    }
    case 'this-week': {
      const day = today.getDay() // 0 Sun .. 6 Sat
      const monday = new Date(y, m, d - ((day + 6) % 7))
      const sunday = new Date(y, m, d + (7 - ((day + 6) % 7)) - 1)
      return { start: toLocalYMD(monday), end: toLocalYMD(sunday) }
    }
    case 'this-month': {
      const first = new Date(y, m, 1)
      const last = new Date(y, m + 1, 0)
      return { start: toLocalYMD(first), end: toLocalYMD(last) }
    }
    case 'last-month': {
      const first = new Date(y, m - 1, 1)
      const last = new Date(y, m, 0)
      return { start: toLocalYMD(first), end: toLocalYMD(last) }
    }
    case 'ytd': {
      const first = new Date(y, 0, 1)
      const now = new Date(y, m, d)
      return { start: toLocalYMD(first), end: toLocalYMD(now) }
    }
    case 'custom':
      return null
  }
}

// Human-readable relative time (Thai).
export const formatRelativeTime = (epochMs: number | null): string => {
  if (epochMs == null) return '—'
  const diff = Date.now() - epochMs
  const sec = Math.floor(diff / 1000)
  if (sec < 10) return 'เมื่อกี้'
  if (sec < 60) return `${sec} วินาทีที่แล้ว`
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min} นาทีที่แล้ว`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr} ชั่วโมงที่แล้ว`
  const day = Math.floor(hr / 24)
  return `${day} วันที่แล้ว`
}

// Buddhist month names (short) — used by target grid.
export const MONTH_SHORT_TH = [
  'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
  'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.',
]

// "1 เม.ย. 2569" / "เม.ย. 2569" — Buddhist-year Thai short-month format used
// across the system. Accepts a YYYY-MM-DD day bucket or a YYYY-MM month bucket.
// Falls back to the raw bucket string when it's neither (e.g. an ISO week
// bucket "YYYY-Www" which the trend chart sometimes emits).
export const formatDateThaiShort = (bucket: string): string => {
  if (!bucket) return ''
  const parts = bucket.split('-').map(Number)
  const [y, m, d] = parts
  if (!Number.isFinite(y) || !Number.isFinite(m) || m < 1 || m > 12) return bucket
  const month = MONTH_SHORT_TH[m - 1]
  const be = y + 543
  if (Number.isFinite(d) && d >= 1 && d <= 31) return `${d} ${month} ${be}`
  return `${month} ${be}`
}

// "1 เม.ย. 2569 – 30 เม.ย. 2569" for a date range, Buddhist year.
// Collapses same-day ranges to a single date. Used by EmptyState to tell the
// user *which* window returned no data.
export const formatDateRange = (startISO: string, endISO: string): string => {
  if (startISO === endISO) return formatDateThaiShort(startISO)
  return `${formatDateThaiShort(startISO)} – ${formatDateThaiShort(endISO)}`
}

// ---- Phase 1A helpers ----------------------------------------

// Count of inclusive days between two YYYY-MM-DD strings.
export const diffDaysInclusive = (from: string, to: string): number => {
  const [fy, fm, fd] = from.split('-').map(Number)
  const [ty, tm, td] = to.split('-').map(Number)
  const fromLocal = new Date(fy, fm - 1, fd).getTime()
  const toLocal = new Date(ty, tm - 1, td).getTime()
  return Math.floor((toLocal - fromLocal) / 86400000) + 1
}

// Exported copy of the file-private toLocalYMD so per-widget hooks can use it.
export const toLocalYMDExport = (d: Date): string => {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

// Add N days (may be negative) to a YYYY-MM-DD string, preserving local date.
export const addDaysYMD = (ymd: string, n: number): string => {
  const [y, m, d] = ymd.split('-').map(Number)
  const dt = new Date(y, m - 1, d + n)
  return toLocalYMDExport(dt)
}

// Auto time-granularity rule (BRD §11.2): day ≤31, week ≤90, month otherwise.
export const resolveGranularity = (from: string, to: string): Granularity => {
  const days = diffDaysInclusive(from, to)
  if (days <= 31) return 'day'
  if (days <= 90) return 'week'
  return 'month'
}

// Same-duration-before: previous range has the same inclusive length.
// e.g. [2026-04-01 .. 2026-04-17] (17d) → [2026-03-15 .. 2026-03-31]
export const previousRange = (from: string, to: string): { from: string; to: string } => {
  const days = diffDaysInclusive(from, to)
  return { from: addDaysYMD(from, -days), to: addDaysYMD(to, -days) }
}

// BRD §17.3 achievement colour.
export type AchievementColor = 'success' | 'warning' | 'error' | 'default'
export const achievementColor = (pct: number | null | undefined): AchievementColor => {
  if (pct == null) return 'default'
  if (pct >= 100) return 'success'
  if (pct >= 70) return 'warning'
  return 'error'
}

// Format variance decimal (e.g. 0.15) as "+15%" / "-3.2%" / "—".
export const formatVariance = (v: number | null | undefined): string => {
  if (v == null) return '—'
  const pct = v * 100
  const sign = pct > 0 ? '+' : ''
  return `${sign}${pct.toFixed(1)}%`
}
